import MergeCompanies from './MergeCompanies';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState, Stat } from "../_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignaturePad } from "@/components/SignaturePad";
import { Mountain, Truck, Plus, Search, FileDown, Trash2, Pencil, CheckCircle2, Camera, PenLine, X, Image as ImageIcon, QrCode, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { BillingBadge, BillingSettings, InvoiceReview, XeroBadge, invoiceStatusLabel } from './LandfillBilling';
import { useAuth } from '@/lib/auth';
import RegistrationInput from './RegistrationInput';
import TruckReassign from './TruckReassign';

const fileUrl = (p) => `${api.defaults.baseURL}/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`;

const SOIL_TYPES = [
  { v: "topsoil", l: "Topsoil" },
  { v: "clay", l: "Clay" },
  { v: "mixed", l: "Mixed topsoil/clay" },
];
const TRUCK_SIZES = [
  { v: "6w", l: "6 wheeler" },
  { v: "8w", l: "8 wheeler" },
  { v: "truck_trailer", l: "Truck and trailer" },
];
const CONDITIONS = [
  { v: "dry", l: "Dry" },
  { v: "damp", l: "Damp" },
  { v: "wet", l: "Wet" },
  { v: "unsuitable", l: "Unsuitable" },
];
const SOIL_L = Object.fromEntries(SOIL_TYPES.map((s) => [s.v, s.l]));
const SIZE_L = Object.fromEntries(TRUCK_SIZES.map((s) => [s.v, s.l]));
const COND_L = Object.fromEntries(CONDITIONS.map((s) => [s.v, s.l]));

const condTone = (c) => (c === "unsuitable" ? "bg-red-100 text-red-700 border-red-200"
  : c === "wet" ? "bg-amber-100 text-amber-700 border-amber-200"
  : "bg-emerald-100 text-emerald-700 border-emerald-200");

const normRego = (r) => (r || "").toUpperCase().replace(/\s+/g, "");
const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const emptyForm = { rego: "", company: "", client_id: null, soil_type: "", truck_size: "", condition: "", note: "", driver_name: "", cost: "", photo_paths: [], signature_path: null };

const money = (n) => (n === null || n === undefined || n === "") ? "—" : `$${Number(n).toFixed(2)}`;

async function uploadLandfillFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", "landfill");
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.storage_path || data.path || null;
}

export function RecordLoadDialog({ open, onOpenChange, onSaved, editLoad }) {
  const { user } = useAuth();
  const canOverride = ['admin', 'super_admin'].includes(user?.role);
  const [quote, setQuote] = useState(null);
  const [override, setOverride] = useState('');
  const [reason, setReason] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [saveTruck, setSaveTruck] = useState(true);
  const [confirmed, setConfirmed] = useState({ material: true, condition: true });
  const manual = useRef(new Set());
  const [autofilled, setAutofilled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [signing, setSigning] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(editLoad
      ? { rego: editLoad.rego, company: editLoad.company, client_id: editLoad.client_id || null, soil_type: editLoad.soil_type, truck_size: editLoad.truck_size, condition: editLoad.condition, note: editLoad.note || "", driver_name: editLoad.driver_name || "", cost: editLoad.cost ?? "", photo_paths: editLoad.photo_paths || [], signature_path: editLoad.signature_path || null }
      : emptyForm);
    setOverride(''); setReason(''); setQuote(null);
    setAutofilled(false);
    setSigning(false);
    setSelectedTruck(null); setSaveTruck(true); manual.current.clear();
    setConfirmed({ material: true, condition: true });
  }, [open, editLoad, user?.org_id]);

  useEffect(() => {
    if (!open) return;
    let current = true;
    setCarriers([]);
    const timer = setTimeout(() => api.get('/landfill/carriers', { params: { q: form.company }, __flSkipReadCache: true })
      .then(({ data }) => { if (current) setCarriers(data); }).catch(() => {}), 150);
    return () => { current = false; clearTimeout(timer); };
  }, [open, user?.org_id, form.company]);

  useEffect(() => {
    if (!open) return;
    let current = true;
    const keys = ['company', 'soil_type', 'truck_size', 'condition'];
    if (editLoad?.cost != null && keys.every(k => form[k] === editLoad[k])) {
      setQuote({ ...(editLoad.pricing_snapshot || {}), applied_rate: editLoad.pricing_snapshot?.final_charge ?? editLoad.cost, saved: true, legacy: !editLoad.pricing_snapshot });
      return;
    }
    setQuote(null);
    if (!keys.every(k => form[k])) return;
    const timer = setTimeout(() => api.post('/landfill/rates/quote', Object.fromEntries(keys.map(k => [k, form[k]])))
      .then(r => { if (current) setQuote(r.data); })
      .catch(() => { if (current) setQuote({ error: 'Could not resolve rate. Try again.' }); }), 200);
    return () => { current = false; clearTimeout(timer); };
  }, [open, editLoad, form.company, form.soil_type, form.truck_size, form.condition]);

  const onRegoChange = (raw) => {
    setSelectedTruck(null); setAutofilled(false);
    setForm(f => ({ ...f, rego: normRego(raw),
      company: manual.current.has('company') ? f.company : '',
      truck_size: manual.current.has('truck_size') ? f.truck_size : '' }));
  };
  const selectTruck = truck => {
    setSelectedTruck(truck); setAutofilled(true);
    setForm(f => ({ ...f, rego: truck.rego,
      company: truck.company || '',
      truck_size: manual.current.has('truck_size') ? f.truck_size : truck.truck_size || '' }));
  };
  const previous = selectedTruck?.previous_load;
  const usePrevious = () => {
    setForm(f => ({ ...f,
      soil_type: manual.current.has('soil_type') ? f.soil_type : previous.soil_type || '',
      condition: manual.current.has('condition') ? f.condition : previous.condition || '',
      truck_size: manual.current.has('truck_size') ? f.truck_size : selectedTruck.truck_size || previous.truck_size || '' }));
    setConfirmed({ material: false, condition: false });
  };

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const p = await uploadLandfillFile(f);
        if (p) paths.push(p);
      }
      setForm((f) => ({ ...f, photo_paths: [...f.photo_paths, ...paths] }));
    } catch {
      toast.error("Could not upload photo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (p) => setForm((f) => ({ ...f, photo_paths: f.photo_paths.filter((x) => x !== p) }));

  const onSignatureSave = async (dataUrl) => {
    setSigning(false);
    if (!dataUrl) return;
    setUploading(true);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "signature.png", { type: "image/png" });
      const p = await uploadLandfillFile(file);
      setForm((f) => ({ ...f, signature_path: p }));
      toast.success("Signature captured");
    } catch {
      toast.error("Could not save signature");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.rego.trim() || !form.company.trim() || !form.soil_type || !form.truck_size || !form.condition) {
      toast.error("Rego, carrier/customer, material, truck size and condition are all required");
      return;
    }
    if (!confirmed.material || !confirmed.condition) {
      toast.error('Confirm material and condition for this load'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, rego: normRego(form.rego), company: form.company.trim(), note: form.note.trim() || null, driver_name: form.driver_name.trim() || null, cost: form.cost === "" || form.cost === null ? null : Number(form.cost) };
      delete payload.cost;
      // Billing associations are managed separately; edits retain stored IDs.
      delete payload.client_id;
      if (!editLoad) payload.save_truck = saveTruck;
      if (override !== '') { payload.override_amount = override; payload.override_reason = reason; }
      let saved;
      if (editLoad) saved = (await api.patch(`/landfill/loads/${editLoad.id}`, payload)).data;
      else saved = (await api.post("/landfill/loads", payload)).data;
      toast.success(editLoad ? "Load updated" : `Load recorded · Docket ${saved?.docket_number || ""}`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save load");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="landfill-record-dialog">
        <DialogHeader>
          <DialogTitle>{editLoad ? `Edit docket ${editLoad.docket_number || ""}` : "Record incoming load"}</DialogTitle>
          <DialogDescription>Each load is saved as a docket for easy invoicing. Capture the truck and soil details as the load arrives.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0">
              <Label htmlFor="lf-rego">Truck rego</Label>
              <RegistrationInput key={user?.org_id} value={form.rego} disabled={!!editLoad} onChange={onRegoChange} onSelect={selectTruck} />
              {autofilled && (
                <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600" data-testid="landfill-autofill-note">
                  <CheckCircle2 className="h-3 w-3" /> Truck selected
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="lf-company">Carrier / customer</Label>
              <Input id="lf-company" data-testid="landfill-company-input" list="lf-carriers"
                className="min-h-11" maxLength={160} autoComplete="off" value={form.company}
                placeholder="Search or enter carrier/customer" onChange={e => {
                  manual.current.add('company'); setForm(f => ({ ...f, company: e.target.value }));
                }} />
              {carriers.filter(name => name !== form.company && name.replace(/\s/g, '').toLowerCase() === form.company.replace(/\s/g, '').toLowerCase()).map(name => <button type="button" key={name} className="block text-sm text-blue-700" onClick={()=>{manual.current.add('company');setForm(f=>({...f,company:name}));}}>Possible existing carrier: {name}</button>)}
              <datalist id="lf-carriers">{carriers.map(name => <option key={name} value={name} />)}</datalist>
            </div>
          </div>
          {!editLoad && previous && <div className="rounded border p-3 text-sm space-y-2">
            <p>Previous load: {SOIL_L[previous.soil_type] || 'Material unknown'} · {COND_L[previous.condition] || 'Condition unknown'} · {SIZE_L[previous.truck_size] || 'Truck size unknown'}</p>
            <Button type="button" variant="outline" className="min-h-11" onClick={usePrevious}>Use previous load details</Button>
            <p className="text-xs">Confirm material and condition for this load. Current rates apply.</p>
          </div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Material</Label>
              <Select value={form.soil_type} onValueChange={(v) => { manual.current.add('soil_type'); setForm((f) => ({ ...f, soil_type: v })); setConfirmed(c => ({ ...c, material: true })); }}>
                <SelectTrigger className="min-h-11" data-testid="landfill-soil-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{SOIL_TYPES.map((s) => <SelectItem key={s.v} value={s.v} data-testid={`landfill-soil-${s.v}`}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Truck size</Label>
              <Select value={form.truck_size} onValueChange={(v) => { manual.current.add('truck_size'); setForm((f) => ({ ...f, truck_size: v })); }}>
                <SelectTrigger className="min-h-11" data-testid="landfill-size-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{TRUCK_SIZES.map((s) => <SelectItem key={s.v} value={s.v} data-testid={`landfill-size-${s.v}`}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Condition</Label>
            <Select value={form.condition} onValueChange={(v) => { manual.current.add('condition'); setForm((f) => ({ ...f, condition: v })); setConfirmed(c => ({ ...c, condition: true })); }}>
              <SelectTrigger className="min-h-11" data-testid="landfill-condition-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{CONDITIONS.map((s) => <SelectItem key={s.v} value={s.v} data-testid={`landfill-condition-${s.v}`}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lf-driver">Driver name <span className="text-zinc-400">(optional)</span></Label>
              <Input id="lf-driver" data-testid="landfill-driver-input" value={form.driver_name}
                onChange={(e) => setForm((f) => ({ ...f, driver_name: e.target.value }))} placeholder="Driver on this load" />
            </div>
            <div>
              <Label htmlFor="lf-cost">Calculated charge</Label>
              <Input id="lf-cost" readOnly value={quote?.applied_rate != null ? money(quote.applied_rate) : ''} placeholder="Select load details" />
              <p className="text-xs" role="status">{quote?.error || (quote?.rate_source === 'missing' && !quote.saved ? 'Rate not configured — administrator review required' : quote ? `${quote.saved ? 'Saved' : quote.rate_source === 'customer' ? 'Customer-specific' : 'Standard'} rate · ${quote.legacy ? 'Legacy saved charge' : quote.tax.gst_registered ? `GST ${quote.tax.pricing}` : 'No GST'}${quote.rate_version ? ` · v${quote.rate_version}` : ''}` : 'Waiting for rate')}</p>
              {canOverride && <details className="mt-2 text-sm"><summary>Override charge</summary><label>Override amount<Input type="number" min="0" step="0.01" value={override} onChange={e => setOverride(e.target.value)} /></label><label>Reason<Input maxLength={1000} value={reason} onChange={e => setReason(e.target.value)} /></label></details>}
            </div>
          </div>
          {!editLoad && <label className="flex min-h-11 items-center gap-2 text-sm">
            <input type="checkbox" checked={saveTruck} onChange={e => setSaveTruck(e.target.checked)} />
            Save truck details to register when recording this load
          </label>}
          {!confirmed.material && <Button type="button" className="min-h-11" variant="outline" disabled={!form.soil_type} onClick={() => setConfirmed(c => ({ ...c, material: true }))}>Confirm material: {SOIL_L[form.soil_type] || 'Select material'}</Button>}
          {!confirmed.condition && <Button type="button" className="min-h-11" variant="outline" disabled={!form.condition} onClick={() => setConfirmed(c => ({ ...c, condition: true }))}>Confirm condition: {COND_L[form.condition] || 'Select condition'}</Button>}

          {/* Photos (optional) */}
          <div>
            <Label>Photos <span className="text-zinc-400">(optional)</span></Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {form.photo_paths.map((p) => (
                <div key={p} className="relative h-16 w-16">
                  <img src={fileUrl(p)} alt="load" className="h-16 w-16 rounded border border-zinc-200 object-cover" />
                  <button type="button" onClick={() => removePhoto(p)} data-testid="landfill-remove-photo"
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-zinc-900 p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="landfill-add-photo-btn"
                className="flex h-16 w-16 flex-col items-center justify-center rounded border border-dashed border-zinc-300 text-zinc-400 hover:border-zinc-400">
                <Camera className="h-5 w-5" /><span className="text-[10px]">Add</span>
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={onPickPhotos} data-testid="landfill-photo-input" />
            </div>
          </div>

          {/* Driver signature (optional) */}
          <div>
            <Label>Driver signature <span className="text-zinc-400">(optional)</span></Label>
            {signing ? (
              <div className="mt-1" data-testid="landfill-signature-pad">
                <SignaturePad onSave={onSignatureSave} onCancel={() => setSigning(false)} testIdPrefix="landfill-sig" />
              </div>
            ) : form.signature_path ? (
              <div className="mt-1 flex items-center gap-3">
                <img src={fileUrl(form.signature_path)} alt="signature" className="h-16 rounded border border-zinc-200 bg-white" data-testid="landfill-signature-preview" />
                <Button type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, signature_path: null }))} data-testid="landfill-clear-signature-btn">Clear</Button>
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => setSigning(true)} data-testid="landfill-sign-btn">
                <PenLine className="mr-2 h-4 w-4" />Capture signature
              </Button>
            )}
          </div>

          <div>
            <Label htmlFor="lf-note">Note <span className="text-zinc-400">(optional)</span></Label>
            <Textarea id="lf-note" data-testid="landfill-note-input" rows={2} value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. visible contamination" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="landfill-cancel-btn">Cancel</Button>
          <Button className="min-h-11" onClick={save} disabled={saving || uploading || !confirmed.material || !confirmed.condition || (override !== '' ? !reason.trim() : (!quote || quote.error || (quote.rate_source === 'missing' && !quote.saved)))} data-testid="landfill-save-btn">{saving ? "Saving…" : uploading ? "Uploading…" : editLoad ? "Save changes" : "Record load"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-zinc-100 py-2 text-sm last:border-0">
      <span className="text-zinc-500">{label}</span>
      <span className="text-right font-medium text-zinc-900">{children}</span>
    </div>
  );
}

function LoadDetailDialog({ load, open, onOpenChange, onEdit }) {
  if (!load) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="landfill-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-[#FF5F15]">{load.docket_number || "—"}</span>
            <Badge variant="outline" className={condTone(load.condition)}>{COND_L[load.condition]}</Badge>
          </DialogTitle>
          <DialogDescription>{fmtTime(load.logged_at)}{load.logged_by_name ? ` · logged by ${load.logged_by_name}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-0">
          <DetailRow label="Rego">{load.rego}</DetailRow>
          <DetailRow label="Company">{load.company}</DetailRow>
          <DetailRow label="Driver">{load.driver_name || "—"}</DetailRow>
          <DetailRow label="Soil type">{SOIL_L[load.soil_type]}</DetailRow>
          <DetailRow label="Truck size">{SIZE_L[load.truck_size]}</DetailRow>
          <DetailRow label="Condition">{COND_L[load.condition]}</DetailRow>
          <DetailRow label="Cost">{money(load.cost)}</DetailRow>
          {load.pricing_snapshot && <DetailRow label="Applied rate">{load.pricing_snapshot.rate_source} · v{load.pricing_snapshot.rate_version || '—'} · {load.pricing_snapshot.tax.pricing} GST{load.pricing_snapshot.override && ` · Override: ${load.pricing_snapshot.override.reason} (${load.pricing_snapshot.override.by}, ${load.pricing_snapshot.override.at})`}</DetailRow>}
          {load.note && <DetailRow label="Note">{load.note}</DetailRow>}
        </div>
        {!!(load.photo_paths?.length) && (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase text-zinc-500">Photos</p>
            <div className="flex flex-wrap gap-2" data-testid="landfill-detail-photos">
              {load.photo_paths.map((p) => (
                <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer">
                  <img src={fileUrl(p)} alt="load" className="h-20 w-20 rounded border border-zinc-200 object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}
        {load.signature_path && (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase text-zinc-500">Driver signature</p>
            <img src={fileUrl(load.signature_path)} alt="signature" className="h-20 rounded border border-zinc-200 bg-white" data-testid="landfill-detail-signature" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="landfill-detail-close">Close</Button>
          <Button onClick={() => { onOpenChange(false); onEdit?.(load); }} data-testid="landfill-detail-edit"><Pencil className="mr-2 h-4 w-4" />Edit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QrIntakeDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/landfill/intake-link").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open]);

  const copy = () => { if (data?.url) { navigator.clipboard?.writeText(data.url); toast.success("Link copied"); } };
  const regenerate = async () => {
    if (!window.confirm("Generate a new QR? The old printed QR codes will stop working.")) return;
    setLoading(true);
    try { const r = await api.post("/landfill/intake-link/regenerate"); setData(r.data); toast.success("New QR generated"); }
    catch { toast.error("Could not regenerate"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="landfill-qr-dialog">
        <DialogHeader>
          <DialogTitle>Driver check-in QR</DialogTitle>
          <DialogDescription>Print this and display it at the gate. Drivers scan it to enter their own rego, company, load and signature — you add the photo and cost afterwards.</DialogDescription>
        </DialogHeader>
        {loading && !data ? <div className="py-10 text-center text-sm text-zinc-500">Loading…</div> : data ? (
          <div className="space-y-4">
            <div className="flex justify-center rounded-xl border border-zinc-200 bg-white p-4">
              {data.qr_data_url ? <img src={data.qr_data_url} alt="Driver intake QR" className="h-56 w-56" data-testid="landfill-qr-image" /> : <p className="text-sm text-zinc-500">QR unavailable</p>}
            </div>
            <div className="flex items-center gap-2">
              <Input readOnly value={data.url} className="text-xs" data-testid="landfill-qr-url" />
              <Button variant="outline" size="icon" onClick={copy} data-testid="landfill-qr-copy"><Copy className="h-4 w-4" /></Button>
            </div>
            <Button variant="ghost" size="sm" onClick={regenerate} className="text-zinc-500" data-testid="landfill-qr-regenerate"><RefreshCw className="mr-2 h-4 w-4" />Generate new QR</Button>
          </div>
        ) : <div className="py-10 text-center text-sm text-zinc-500">Could not load the intake link.</div>}
      </DialogContent>
    </Dialog>
  );
}

export function LandfillOverview() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [detailLoad, setDetailLoad] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/landfill/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = summary || {};
  return (
    <div className="space-y-6" data-testid="landfill-overview">
      <PageHeader tag="Landfill Operations" title="Tip face overview" subtitle="Today's incoming dirt loads at a glance."
        action={<Button onClick={() => setDialog(true)} data-testid="landfill-record-btn"><Plus className="mr-2 h-4 w-4" />Record load</Button>} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Loads today" value={s.today_count ?? 0} accent />
        <Stat label="Cost today" value={money(s.today_cost)} />
        <Stat label="Topsoil today" value={s.by_soil_type?.topsoil ?? 0} />
        <Stat label="Clay today" value={s.by_soil_type?.clay ?? 0} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="landfill-condition-breakdown">
          <h3 className="text-sm font-semibold text-zinc-900">Condition (today)</h3>
          <div className="mt-3 space-y-2">
            {CONDITIONS.map((c) => (
              <div key={c.v} className="flex items-center justify-between text-sm">
                <Badge variant="outline" className={condTone(c.v)}>{c.l}</Badge>
                <span className="font-semibold">{s.by_condition?.[c.v] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="landfill-size-breakdown">
          <h3 className="text-sm font-semibold text-zinc-900">Truck size (today)</h3>
          <div className="mt-3 space-y-2">
            {TRUCK_SIZES.map((c) => (
              <div key={c.v} className="flex items-center justify-between text-sm">
                <span className="text-zinc-600">{c.l}</span>
                <span className="font-semibold">{s.by_truck_size?.[c.v] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white">
        <div className="border-b border-zinc-100 px-5 py-3 text-sm font-semibold text-zinc-900">Recent loads today</div>
        {loading ? <div className="p-6 text-sm text-zinc-500">Loading…</div>
          : !s.recent?.length ? <EmptyState icon={Mountain} title="No loads yet today" description="Record the first incoming load to get started." />
          : <ul className="divide-y divide-zinc-100" data-testid="landfill-recent-list">
              {s.recent.map((l) => (
                <li key={l.id} onClick={() => setDetailLoad(l)} className="flex cursor-pointer items-center justify-between px-5 py-3 text-sm hover:bg-zinc-50" data-testid={`landfill-recent-${l.id}`}>
                  <div>
                    <span className="font-mono text-xs font-semibold text-[#FF5F15]">{l.docket_number}</span>
                    <span className="ml-2 font-semibold text-zinc-900">{l.rego}</span>
                    <span className="text-zinc-500"> · {l.company}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{SOIL_L[l.soil_type]}</Badge>
                    <Badge variant="outline" className={condTone(l.condition)}>{COND_L[l.condition]}</Badge>
                    {l.cost != null && <span className="text-xs font-medium text-zinc-700">{money(l.cost)}</span>}
                    <span className="hidden text-xs text-zinc-400 sm:inline">{fmtTime(l.logged_at)}</span>
                  </div>
                </li>
              ))}
            </ul>}
      </div>

      <RecordLoadDialog open={dialog} onOpenChange={setDialog} onSaved={load} />
      <LoadDetailDialog load={detailLoad} open={!!detailLoad} onOpenChange={(v) => !v && setDetailLoad(null)} />
    </div>
  );
}

export function LandfillLoads() {
  const { user } = useAuth();
  const [billingFilter, setBillingFilter] = useState('all');
  const [invoiceId, setInvoiceId] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [soil, setSoil] = useState("all");
  const [cond, setCond] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [editLoad, setEditLoad] = useState(null);
  const [detailLoad, setDetailLoad] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [tipsites, setTipsites] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [assignTo, setAssignTo] = useState("");
  const loadTipsites = useCallback(() => {
    api.get('/landfill/tipsites').then((r) => setTipsites(r.data?.tipsites || [])).catch(() => setTipsites([]));
  }, []);
  useEffect(() => { loadTipsites(); }, [loadTipsites]);
  const tipsiteName = useMemo(() => Object.fromEntries(tipsites.map((t) => [t.id, t.name])), [tipsites]);
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const assignSelected = async () => {
    if (!assignTo || !selected.size) return;
    try {
      const r = await api.post(`/landfill/tipsites/${assignTo}/assign-loads`, { load_ids: [...selected] });
      toast.success(`Assigned ${r.data.assigned} load${r.data.assigned === 1 ? "" : "s"}`);
      setSelected(new Set()); setAssignTo(""); load(); loadTipsites();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not assign'); }
  };
  const reconcile = async (id) => {
    const reason = window.prompt('Review evidence outside Fieldlink first. Enter the reason this load is confirmed uninvoiced:');
    if (!reason || reason.trim().length < 5) return;
    try { await api.post(`/landfill/loads/${id}/reconcile`, { reason }); toast.success('Load reviewed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not reconcile load'); }
  };

  const params = useMemo(() => {
    const p = {};
    if (search.trim()) p.search = search.trim();
    if (soil !== "all") p.soil_type = soil;
    if (cond !== "all") p.condition = cond;
    if (billingFilter !== 'all') p.billing_state = billingFilter;
    return p;
  }, [search, soil, cond, billingFilter]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/landfill/loads", { params: { ...params, limit: 100 } })
      .then((r) => setData(r.data || { items: [], total: 0 }))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this load?")) return;
    try { await api.delete(`/landfill/loads/${id}`); toast.success("Load deleted"); load(); }
    catch { toast.error("Could not delete load"); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/landfill/loads/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "landfill-loads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export"); }
  };

  return (
    <div className="space-y-5" data-testid="landfill-loads">
      <PageHeader tag="Landfill Operations" title="Incoming loads" subtitle="Every dirt load recorded at the tip face."
        action={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setQrOpen(true)} data-testid="landfill-qr-btn"><QrCode className="mr-2 h-4 w-4" />Driver QR</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="landfill-export-btn"><FileDown className="mr-2 h-4 w-4" />Export CSV</Button>
          <Button onClick={() => { setEditLoad(null); setDialog(true); }} data-testid="landfill-record-btn"><Plus className="mr-2 h-4 w-4" />Record load</Button>
        </div>} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input className="pl-9" placeholder="Search rego or company" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="landfill-search-input" />
        </div>
        <Select value={billingFilter} onValueChange={setBillingFilter}>
          <SelectTrigger className="w-[200px]" aria-label="Billing status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All billing states</SelectItem><SelectItem value="uninvoiced">Uninvoiced</SelectItem><SelectItem value="draft">Draft invoice</SelectItem><SelectItem value="invoiced">Invoiced</SelectItem><SelectItem value="needs_reconciliation">Needs reconciliation</SelectItem></SelectContent>
        </Select>
        <Select value={soil} onValueChange={setSoil}>
          <SelectTrigger className="w-[180px]" data-testid="landfill-soil-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All soil types</SelectItem>
            {SOIL_TYPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={cond} onValueChange={setCond}>
          <SelectTrigger className="w-[160px]" data-testid="landfill-condition-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All conditions</SelectItem>
            {CONDITIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#FF5F15]/40 bg-[#FF5F15]/5 p-3" data-testid="landfill-bulk-assign-bar">
          <span className="text-sm font-semibold">{selected.size} load{selected.size === 1 ? "" : "s"} selected</span>
          <Select value={assignTo} onValueChange={setAssignTo}>
            <SelectTrigger className="w-[240px]" data-testid="landfill-assign-tipsite-select"><SelectValue placeholder="Assign to tipsite…" /></SelectTrigger>
            <SelectContent>
              {tipsites.length === 0 ? <div className="px-3 py-2 text-xs text-zinc-500">No tipsites yet — add one first</div>
                : tipsites.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" disabled={!assignTo} onClick={assignSelected} data-testid="landfill-assign-tipsite-btn">Assign</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
        {loading ? <div className="p-6 text-sm text-zinc-500">Loading…</div>
          : !data.items.length ? <EmptyState icon={Truck} title="No loads found" description="Adjust filters or record a new load." />
          : <table className="w-full text-sm" data-testid="landfill-loads-table">
              <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-4 py-2">Docket</th>
                  <th className="px-4 py-2">Time</th>
                  <th className="px-4 py-2">Rego</th>
                  <th className="px-4 py-2">Company</th>
                  <th className="px-4 py-2">Soil</th>
                  <th className="px-4 py-2">Size</th>
                  <th className="px-4 py-2">Tipsite</th>
                  <th className="px-4 py-2">Condition</th>
                  <th className="px-4 py-2">Cost</th>
                  <th className="px-4 py-2">Billing</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.items.map((l) => (
                  <tr key={l.id} data-testid={`landfill-load-row-${l.id}`}
                    onClick={() => setDetailLoad(l)} className="cursor-pointer hover:bg-zinc-50">
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSel(l.id)} data-testid={`landfill-select-${l.id}`} className="h-4 w-4 accent-[#FF5F15]" />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs font-semibold text-[#FF5F15]">{l.docket_number || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-zinc-500">{fmtTime(l.logged_at)}</td>
                    <td className="px-4 py-2 font-semibold text-zinc-900">{l.rego}</td>
                    <td className="px-4 py-2 text-zinc-700">
                      {l.company}
                      {l.source === "driver" && <Badge variant="outline" className="ml-2 border-blue-200 bg-blue-50 text-blue-700 text-[10px]" data-testid={`landfill-driver-badge-${l.id}`}>Driver check-in</Badge>}
                      {l.driver_name && <span className="block text-xs text-zinc-400">Driver: {l.driver_name}</span>}
                      {l.note && <span className="block text-xs text-zinc-400">{l.note}</span>}
                      <span className="mt-0.5 flex items-center gap-2 text-zinc-400">
                        {!!(l.photo_paths?.length) && <span className="flex items-center gap-0.5 text-[11px]" data-testid={`landfill-photos-badge-${l.id}`}><ImageIcon className="h-3 w-3" />{l.photo_paths.length}</span>}
                        {l.signature_path && <span className="flex items-center gap-0.5 text-[11px]" data-testid={`landfill-signed-badge-${l.id}`}><PenLine className="h-3 w-3" />Signed</span>}
                      </span>
                    </td>
                    <td className="px-4 py-2">{SOIL_L[l.soil_type]}</td>
                    <td className="px-4 py-2">{SIZE_L[l.truck_size]}</td>
                    <td className="px-4 py-2 text-xs">{l.tipsite_id ? <span className="text-zinc-600">{tipsiteName[l.tipsite_id] || "Tipsite"}</span> : <span className="text-amber-600">Unassigned</span>}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className={condTone(l.condition)}>{COND_L[l.condition]}</Badge></td>
                    <td className="whitespace-nowrap px-4 py-2 font-medium text-zinc-900">{money(l.cost)}</td>
                    <td className="px-4 py-2" onClick={e => e.stopPropagation()}><BillingBadge state={l.billing_state} />{l.invoice_id && <button className="mt-1 block text-xs text-[#FF5F15] underline" onClick={() => setInvoiceId(l.invoice_id)}>{l.invoice_number}</button>}{l.billing_state === 'needs_reconciliation' && ['admin', 'super_admin'].includes(user?.role) && <button className="mt-1 block text-xs underline" onClick={() => reconcile(l.id)}>Review billing evidence</button>}</td>
                    <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" title="Edit load" disabled={!['uninvoiced', 'released'].includes(l.billing_state)} onClick={() => { setEditLoad(l); setDialog(true); }} data-testid={`landfill-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Delete load" disabled={l.billing_state !== 'uninvoiced'} onClick={() => remove(l.id)} data-testid={`landfill-delete-${l.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
      <p className="text-xs text-zinc-400">{data.total} load{data.total === 1 ? "" : "s"} total</p>

      <RecordLoadDialog open={dialog} onOpenChange={setDialog} onSaved={load} editLoad={editLoad} />
      <LoadDetailDialog load={detailLoad} open={!!detailLoad} onOpenChange={(v) => !v && setDetailLoad(null)} onEdit={(l) => { setEditLoad(l); setDialog(true); }} />
      <QrIntakeDialog open={qrOpen} onOpenChange={setQrOpen} />
      <InvoiceReview id={invoiceId} onClose={() => setInvoiceId(null)} onChanged={load} onPdf={downloadInvoicePdf} />
    </div>
  );
}

export function LandfillTrucks() {
  const { user } = useAuth();
  const canEdit = ["admin", "super_admin"].includes(user?.role);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const loadTrucks = useCallback(() => {
    setLoading(true);
    api.get("/landfill/trucks").then((r) => setTrucks(r.data || [])).catch(() => setTrucks([])).finally(() => setLoading(false));
  }, []);
  useEffect(loadTrucks, [loadTrucks]);

  const filtered = trucks.filter((t) => !q.trim() || t.rego.includes(normRego(q)) || (t.company || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="landfill-trucks">
      <PageHeader tag="Landfill Operations" title="Saved trucks" subtitle="Remembered rego, company and size — used to autofill new loads." />
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <Input className="pl-9" placeholder="Search rego or company" value={q} onChange={(e) => setQ(e.target.value)} data-testid="landfill-trucks-search" />
      </div>
      <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
        {loading ? <div className="p-6 text-sm text-zinc-500">Loading…</div>
          : !filtered.length ? <EmptyState icon={Truck} title="No saved trucks yet" description="Trucks are remembered automatically the first time you record a load." />
          : <table className="w-full text-sm" data-testid="landfill-trucks-table">
              <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-xs uppercase text-zinc-500">
                <tr><th className="px-4 py-2">Rego</th><th className="px-4 py-2">Company</th><th className="px-4 py-2">Size</th><th className="px-4 py-2">Last used</th>{canEdit && <th className="px-4 py-2" />}</tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((t) => (
                  <tr key={t.id || `${t.rego}-${t.company}`} data-testid={`landfill-truck-row-${t.rego}`}>
                    <td className="px-4 py-2 font-semibold text-zinc-900">{t.rego}</td>
                    <td className="px-4 py-2 text-zinc-700">{t.company}</td>
                    <td className="px-4 py-2">{SIZE_L[t.truck_size] || t.truck_size}</td>
                    <td className="px-4 py-2 text-xs text-zinc-500">{fmtTime(t.last_used_at)}</td>
                    {canEdit && <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`landfill-truck-edit-${t.rego}`}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
      <TruckReassign truck={editing} onClose={() => setEditing(null)} onChanged={loadTrucks} />
    </div>
  );
}


function CompanyLoads({ company, from, to, onOpen }) {
  const [loads, setLoads] = useState(null);
  useEffect(() => {
    const p = { company, limit: 200 };
    if (from) p.from = from;
    if (to) p.to = to;
    api.get("/landfill/loads", { params: p }).then((r) => setLoads(r.data?.items || [])).catch(() => setLoads([]));
  }, [company, from, to]);
  if (loads === null) return <div className="px-4 py-3 text-sm text-zinc-500">Loading…</div>;
  if (!loads.length) return <div className="px-4 py-3 text-sm text-zinc-500">No loads in this period.</div>;
  return (
    <table className="w-full text-sm" data-testid={`landfill-company-loads-${company}`}>
      <thead className="bg-zinc-50 text-left text-xs uppercase text-zinc-500">
        <tr><th className="px-4 py-2">Docket</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Rego</th><th className="px-4 py-2">Soil</th><th className="px-4 py-2">Condition</th><th className="px-4 py-2">Cost</th></tr>
      </thead>
      <tbody className="divide-y divide-zinc-100">
        {loads.map((l) => (
          <tr key={l.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => onOpen(l)} data-testid={`landfill-company-load-${l.id}`}>
            <td className="px-4 py-2 font-mono text-xs font-semibold text-[#FF5F15]">{l.docket_number || "—"}</td>
            <td className="whitespace-nowrap px-4 py-2 text-xs text-zinc-500">{fmtTime(l.logged_at)}</td>
            <td className="px-4 py-2 font-semibold text-zinc-900">{l.rego}</td>
            <td className="px-4 py-2">{SOIL_L[l.soil_type]}</td>
            <td className="px-4 py-2"><Badge variant="outline" className={condTone(l.condition)}>{COND_L[l.condition]}</Badge></td>
            <td className="px-4 py-2 font-medium">{money(l.cost)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function LandfillCompanies() {
  const [invoiceId, setInvoiceId] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [companyError, setCompanyError] = useState('');
  const companyRequest = useRef(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detailLoad, setDetailLoad] = useState(null);
  const [invoiceCompany, setInvoiceCompany] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setCompanyError('');
    const requestId = ++companyRequest.current;
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    api.get("/landfill/companies", { params: p, __flSkipReadCache: true })
      .then(r => { if (requestId === companyRequest.current) setRows(r.data || []); })
      .catch(e => { if (requestId === companyRequest.current) {
        setRows([]);
        const detail = e.response?.data?.detail;
        setCompanyError(typeof detail === 'string' ? detail : 'Could not check company loads. Please retry.');
      } }).finally(() => { if (requestId === companyRequest.current) setLoading(false); });
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const totalLoads = rows.reduce((a, r) => a + r.load_count, 0);
  const totalCost = rows.reduce((a, r) => a + (r.total_cost || 0), 0);

  return (
    <div className="space-y-5" data-testid="landfill-companies">
      <PageHeader tag="Landfill Operations" title="Companies" subtitle="Loads tipped by each carrier, with cost — filter by date." />
      <MergeCompanies companies={rows} from={from} to={to} onMerged={()=>{setExpanded(null);load();}} />

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-zinc-200 bg-white p-4">
        <div>
          <Label htmlFor="lf-from" className="text-xs">From</Label>
          <Input id="lf-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="landfill-company-from" className="w-[160px]" />
        </div>
        <div>
          <Label htmlFor="lf-to" className="text-xs">To</Label>
          <Input id="lf-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="landfill-company-to" className="w-[160px]" />
        </div>
        {(from || to) && <Button variant="ghost" size="sm" onClick={() => { setFrom(""); setTo(""); }} data-testid="landfill-company-clear-dates">Clear dates</Button>}
        <div className="ml-auto text-right text-sm">
          <div className="text-zinc-500">{rows.length} companies · {totalLoads} loads</div>
          <div className="font-semibold text-zinc-900">{money(totalCost)} total</div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
        {loading ? <div className="p-6 text-sm text-zinc-500">Loading…</div>
          : companyError ? <div role="alert" className="p-4 text-sm"><p>{companyError}</p><Button variant="outline" onClick={load}>Retry company loads</Button></div>
          : !rows.length ? <EmptyState icon={Truck} title="No loads in this period" description="Adjust the date range to see carrier activity." />
          : <ul className="divide-y divide-zinc-100" data-testid="landfill-companies-list">
              {rows.map((c) => (
                <li key={c.company}>
                  <div onClick={() => setExpanded(expanded === c.company ? null : c.company)}
                    className="flex w-full cursor-pointer items-center justify-between px-5 py-3 text-left hover:bg-zinc-50" data-testid={`landfill-company-row-${c.company}`}>
                    <div>
                      <span className="font-semibold text-zinc-900">{c.company}</span>
                      <span className="block text-xs text-zinc-400">Last load {fmtTime(c.last_load_at)}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-zinc-600">{c.load_count} total loads<span className="block text-xs">{c.ready_count ?? 0} ready to invoice · {c.invoiced_count ?? 0} already invoiced</span>{!!c.eligibility?.claimed_by_draft && <span className="block text-xs">{c.eligibility.claimed_by_draft} reserved by draft invoices</span>}{!!c.eligibility?.needs_reconciliation && <span className="block text-xs">{c.eligibility.needs_reconciliation} need billing review</span>}</span>
                      <span className="w-20 text-right font-semibold text-zinc-900">{money(c.total_cost)}</span>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setInvoiceCompany(c.company); }} data-testid={`landfill-invoice-btn-${c.company}`}><FileDown className="mr-1 h-4 w-4" />Invoice ({c.ready_count ?? 0} ready)</Button>
                    </div>
                  </div>
                  {expanded === c.company && (
                    <div className="border-t border-zinc-100 bg-zinc-50/50">
                      <CompanyLoads company={c.company} from={from} to={to} onOpen={setDetailLoad} />
                    </div>
                  )}
                </li>
              ))}
            </ul>}
      </div>

      <LoadDetailDialog load={detailLoad} open={!!detailLoad} onOpenChange={(v) => !v && setDetailLoad(null)} />
      <CreateInvoiceDialog key={`${invoiceCompany || "closed"}:${from}:${to}`} initialFrom={from} initialTo={to} company={invoiceCompany} open={!!invoiceCompany} onOpenChange={(v) => !v && setInvoiceCompany(null)} onCreated={invoice => { load(); setInvoiceId(invoice.id); }} />
      <InvoiceReview id={invoiceId} onClose={() => setInvoiceId(null)} onChanged={load} onPdf={downloadInvoicePdf} />
    </div>
  );
}

// Date arithmetic uses NZ calendar dates, independent of the browser timezone.
export function periodRange(preset, now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now).map(p => [p.type, p.value]));
  const today = `${parts.year}-${parts.month}-${parts.day}`;
  const day = new Date(`${today}T00:00:00Z`);
  const iso = d => d.toISOString().slice(0, 10);
  if (preset === 'day') return {from: today, to: today};
  if (preset === 'week') {
    day.setUTCDate(day.getUTCDate() - (day.getUTCDay() + 6) % 7);
    const from = iso(day);
    day.setUTCDate(day.getUTCDate() + 6);
    return {from, to: iso(day)};
  }
  if (preset === 'month') {
    return {from: `${parts.year}-${parts.month}-01`, to: iso(new Date(Date.UTC(Number(parts.year), Number(parts.month), 0)))};
  }
  return {from: '', to: ''};
}

export async function downloadInvoicePdf(id, { hideDocketPrice = false } = {}) {
  // Reserve the tab during the click; browsers can block popups after an API wait.
  const tab = window.open('', '_blank');
  if (tab) {
    tab.opener = null;
    tab.document.title = 'Preparing invoice PDF';
    tab.document.body.textContent = 'Preparing invoice and attached load dockets…';
  }
  try {
    const r = await api.get(`/landfill/invoices/${id}/pdf`, { params: hideDocketPrice ? { hide_docket_price: true } : {}, responseType: "blob", __flSkipReadCache: true, timeout: 45000 });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    if (tab && !tab.closed) tab.location.href = url;
    else {
      const link = document.createElement('a');
      link.href = url; link.download = 'landfill-invoice.pdf';
      document.body.appendChild(link); link.click(); link.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (error) {
    if (tab && !tab.closed) tab.close();
    let data = error.response?.data;
    if (data instanceof Blob) {
      try { data = JSON.parse(await data.text()); } catch { data = null; }
    }
    const detail = typeof data?.detail === 'string' ? data.detail : null;
    const status = error.response?.status;
    toast.error(detail || (status ? `Invoice PDF failed (HTTP ${status}). Please retry.` : 'Invoice PDF could not be downloaded. Check your connection and retry.'));
  }
}

export function CreateInvoiceDialog({ company, open, onOpenChange, onCreated, initialFrom = '', initialTo = '' }) {
  const [selected, setSelected] = useState(null);
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const request = useRef(null);
  const [preset, setPreset] = useState(initialFrom || initialTo ? 'custom' : 'ready');
  const [range, setRange] = useState({from: initialFrom, to: initialTo});
  const [previewError, setPreviewError] = useState('');
  const [creationError, setCreationError] = useState('');
  const [refreshPreview, setRefreshPreview] = useState(0);
  const creatingRef = useRef(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  useEffect(() => { setSelected(null); setPreview(null); request.current = null; }, [open, company, range]);

  const changePeriod = value => {
    setPreset(value);
    if (value !== 'custom') setRange(periodRange(value));
  };

  useEffect(() => {
    if (!open || !company) return;
    let current = true;
    setLoading(true);
    const p = { company };
    if (range.from) p.from = range.from;
    if (range.to) p.to = range.to;
    if (selected !== null) p.load_ids = selected.join(',');
    setPreviewError(''); setPreview(null);
    api.get("/landfill/invoices/preview", { params: p, __flSkipReadCache: true })
      .then((r) => { if (current) setPreview(r.data); })
      .catch(e => { if (current) {
        const detail = e.response?.data?.detail;
        setPreviewError(typeof detail === 'string' ? detail : 'Could not check invoice eligibility. Please retry. No invoice was created.');
        setPreview(null);
      } }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [open, company, range, selected, refreshPreview]);

  const create = async () => {
    if (creatingRef.current || loading || !preview?.count || preview.can_create === false) return;
    creatingRef.current = true;
    setCreating(true); setCreationError('');
    try {
      const body = { company, load_ids: preview.lines.map(l => l.load_id), billing_address: address, billing_email: email };
      if (range.from) body.from = range.from;
      if (range.to) body.to = range.to;
      const fingerprint = JSON.stringify(body);
      if (request.current?.fingerprint !== fingerprint) request.current = { fingerprint, key: crypto.randomUUID() };
      body.request_key = request.current.key;
      const r = await api.post("/landfill/invoices", body, {__flOnlineOnly: true});
      toast.success(`Invoice ${r.data.invoice_number} created`);
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Invoice creation could not be confirmed. Retry with the same selection.';
      setCreationError(message); toast.error(message);
    } finally { creatingRef.current = false; setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="landfill-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Invoice {company}</DialogTitle>
          <DialogDescription>Draft invoice for {company}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Period</Label>
            <Select value={preset} onValueChange={changePeriod} disabled={creating}>
              <SelectTrigger className="min-h-11" data-testid="landfill-invoice-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ready">Ready loads (all dates)</SelectItem>
                <SelectItem value="day">Today</SelectItem>
                <SelectItem value="week">This week</SelectItem>
                <SelectItem value="month">This month</SelectItem>
                <SelectItem value="custom">Custom dates</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {preset === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">From</Label><Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="landfill-invoice-from" /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="landfill-invoice-to" /></div>
            </div>
          )}
          <div><Label htmlFor="lf-billing-address">Billing address</Label><Textarea id="lf-billing-address" maxLength={1000} value={address} onChange={e => setAddress(e.target.value)} /></div>
          <div><Label htmlFor="lf-billing-email">Billing email</Label><Input id="lf-billing-email" type="email" maxLength={160} value={email} onChange={e => setEmail(e.target.value)} /></div>
          {creationError && <p role="alert" className="text-sm text-amber-700 dark:text-amber-300">{creationError}</p>}
          {previewError && <div role="alert" className="text-sm text-amber-700 dark:text-amber-300"><p>{previewError}</p><Button variant="outline" disabled={loading || creating} onClick={() => setRefreshPreview(v => v + 1)}>Retry invoice preview</Button></div>}
          {preview?.eligibility && <div className="text-sm space-y-1" aria-label="Invoice eligibility">
            <p>{preview.eligibility.total_loads} total loads · {preview.eligibility.ready} ready to invoice in this period</p>
            {Object.entries({already_invoiced: 'already invoiced', claimed_by_draft: 'reserved by draft invoices', outside_period: 'outside selected period', needs_reconciliation: 'need billing review', unpriced: 'need a saved charge', different_billing_customer: 'belong to a separate billing customer'}).map(([key,label]) => preview.eligibility[key] > 0 && <p key={key}>{preview.eligibility[key]} loads {label}</p>)}
          </div>}
          <div className="max-h-48 overflow-y-auto divide-y">{preview?.available_lines?.map(line => <label key={line.load_id} className="flex items-center gap-3 py-2 text-sm"><input type="checkbox" disabled={loading || creating} checked={preview.lines.some(chosen => chosen.load_id === line.load_id)} onChange={e => setSelected(current => { const ids = current ?? preview.lines.map(l => l.load_id); return e.target.checked ? [...ids, line.load_id] : ids.filter(id => id !== line.load_id); })} /><span className="flex-1">{line.docket_number} / {line.rego} · {line.logged_date}</span><span>{money(preview.mixed_tax ? line.pricing_snapshot?.totals?.total ?? line.cost : line.cost)}</span></label>)}</div>
          <div className="border-y border-zinc-200 py-4 text-sm dark:border-zinc-700" data-testid="landfill-invoice-preview">
            {loading ? "Loading…" : preview ? (
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-zinc-500">Dockets</span><span className="font-semibold">{preview.count}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{money(preview.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">{preview.mixed_tax ? 'GST' : preview.gst_registered ? `GST ${preview.gst_rate}% (${preview.pricing})` : 'GST (not registered)'}</span><span>{money(preview.gst)}</span></div>
                <div className="flex justify-between border-t border-zinc-200 pt-1 text-base font-bold"><span>Total</span><span>{money(preview.total)}</span></div>
              </div>
            ) : previewError ? "Invoice eligibility could not be checked." : "Checking invoice eligibility…"}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="landfill-invoice-cancel">Cancel</Button>
          <Button onClick={create} disabled={loading || creating || !preview?.count || preview.can_create === false || preview.count > 500} data-testid="landfill-invoice-create">{creating ? "Creating…" : `Create draft (${preview?.count || 0})`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LandfillInvoices() {
  const { user } = useAuth();
  const [invoiceId, setInvoiceId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/landfill/invoices").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);


  return (
    <div className="space-y-5" data-testid="landfill-invoices">
      <PageHeader tag="Landfill Operations" title="Invoices" subtitle="Invoices generated from load dockets, by company and period." />
      <BillingSettings />
      <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
        {loading ? <div className="p-6 text-sm text-zinc-500">Loading…</div>
          : !rows.length ? <EmptyState icon={FileDown} title="No invoices yet" description="Create one from the Companies page." />
          : <table className="w-full text-sm" data-testid="landfill-invoices-table">
              <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-xs uppercase text-zinc-500">
                <tr><th className="px-4 py-2">Invoice</th><th className="px-4 py-2">Company</th><th className="px-4 py-2">Period</th><th className="px-4 py-2">Total</th><th className="px-4 py-2"></th></tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {rows.map((inv) => (
                  <tr key={inv.id} data-testid={`landfill-invoice-row-${inv.id}`}>
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-[#FF5F15]"><button onClick={() => setInvoiceId(inv.id)} className="underline">{inv.invoice_number}</button><Badge variant="outline" className="mt-1 block w-fit">Fieldlink: {invoiceStatusLabel(inv.status)}</Badge><XeroBadge invoice={inv} />{inv.xero_invoice_number && <span className="block">{inv.xero_invoice_number}</span>}{inv.xero_synced_at && <span className="block">{inv.xero_synced_at}</span>}{inv.xero_payment_status && <span className="block">{inv.xero_payment_status}</span>}</td>
                    <td className="px-4 py-2 font-medium text-zinc-900">{inv.company}</td>
                    <td className="px-4 py-2 text-xs text-zinc-500">{inv.date_from || "—"} → {inv.date_to || "—"}</td>
                    <td className="px-4 py-2 font-semibold">{money(inv.total)}{inv.gst_registered && <span className="ml-1 text-[10px] text-zinc-400">incl GST</span>}</td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="outline" size="sm" onClick={() => downloadInvoicePdf(inv.id)} data-testid={`landfill-invoice-pdf-${inv.id}`}><FileDown className="mr-1 h-4 w-4" />PDF</Button>
                      <Button variant="outline" size="sm" onClick={() => setInvoiceId(inv.id)}>Review</Button>
                      {inv.status === "draft" && inv.billing_version === 2 && <Button size="sm" className="bg-[#FF5F15] text-white hover:bg-[#e55513]" onClick={() => setInvoiceId(inv.id)}>Finalise invoice</Button>}
                      {inv.status === 'draft' && inv.billing_version !== 2 && <><Badge variant="outline">Needs reconciliation</Badge>{['admin','super_admin'].includes(user?.role) ? <Button variant="outline" size="sm" className="text-[#FF5F15]" onClick={()=>setInvoiceId(inv.id)}>Recover invoice</Button> : <span className="text-xs">Administrator recovery required</span>}</>}
                      {['admin', 'super_admin'].includes(user?.role) && inv.status === 'finalised'  && <Button variant="outline" size="sm" className="text-[#FF5F15]" onClick={() => setInvoiceId(inv.id)}>{inv.xero_invoice_id || inv.xero_pending ? 'Reconcile Xero' : 'Send to Xero'}</Button>}
                      {['admin', 'super_admin'].includes(user?.role) && !['cancelled', 'void'].includes(inv.status) && <Button variant="outline" size="sm" onClick={() => setInvoiceId(inv.id)}>Cancel Invoice &amp; Release Dockets</Button>}
                      {inv.xero_invoice_id && <a className="ml-2 text-xs text-[#FF5F15] underline" target="_blank" rel="noreferrer" href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${encodeURIComponent(inv.xero_invoice_id)}`}>Open in Xero</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
      <InvoiceReview id={invoiceId} onClose={() => setInvoiceId(null)} onChanged={load} onPdf={downloadInvoicePdf} />
    </div>
  );
}

// Uses the same load-entry form and session as the office workspace.
export function LandfillOperatorEntry() {
  const [open, setOpen] = useState(false);
  return <div className="p-4 space-y-4">
    <PageHeader title="Landfill Operations" subtitle="Record incoming loads" />
    <Button onClick={() => setOpen(true)}>Record load</Button>
    <RecordLoadDialog open={open} onOpenChange={setOpen} onSaved={() => setOpen(false)} />
  </div>;
}
